import React, { useEffect, useId, useRef, useState } from "react";
import { usePublishPageChrome } from "./pageChrome.jsx";
import { useHistory, useLocation } from "react-router-dom";
import Button from "../ui/Button";
import IconButton from "../ui/IconButton";
import Icon from "../ui/Icon";
import Badge from "../ui/Badge";
import Tabs from "../ui/Tabs";
import Popover from "../ui/Popover";
import useBreakpoint from "../../hooks/useBreakpoint";
import "./PageHeader.scss";

// Fontaine M30 — PageHeader: title row (back · title · badge · description) + optional tabs row + actions.
// Same API as before (17 call sites keep working): title, description, backTo, onBack, showBackButton,
// tabs [{ label, to, exact?, icon?, match?, count? }], actions [{ label, to?, onClick?, variant?, size? }] | node,
// activePath, topbarActions, children. New: badge (count / status), sticky compact mode, actions collapse
// into a ⋯ menu on phones. Underline tabs (Board 2 Q5 = A). Profile / bell moved to the app topbar (M28).

/**
 * @param {string} title
 * @param {string} [description]
 * @param {ReactNode|{status?: string, count?: number, tone?: string}} [badge]   next to the title
 * @param {string} [backTo] / {() => void} [onBack] / {boolean} [showBackButton=true]
 * @param {Array} [tabs]
 * @param {Array|ReactNode} [actions]
 * @param {string} [activePath]
 * @param {ReactNode} [topbarActions]     controls on the right of the title row (filters, view switch)
 * @param {boolean} [sticky=true]
 * @param {boolean} [compactOnScroll=true] false keeps the title at full size while scrolled
 */
const PageHeader = ({ title, description, badge, backTo, onBack, showBackButton = true, tabs, actions, activePath, topbarActions, sticky = true, compactOnScroll = true, className = "", subBarClassName = "", children }) => {
  const history = useHistory();
  const { pathname } = useLocation();
  const { isPhone, isAtLeastMd } = useBreakpoint();
  // Identifies this header to the page-chrome registry — see pageChrome.jsx.
  const chromeId = useId();
  const sentinel = useRef(null);
  const headerRef = useRef(null);
  const [compact, setCompact] = useState(false);

  // `compactOnScroll={false}` opts a page out of the shrink-on-scroll title.
  // The listing flows (Edit Listing / New Listing / New Stock / New
  // Consignment) use it: their header sits directly above a tall accordion, so
  // the h2 → h3 resize fired on every scroll and read as the title glitching
  // rather than as chrome settling. Default stays on for every other page.
  useEffect(() => {
    if (!sticky || !compactOnScroll || !sentinel.current || typeof IntersectionObserver === "undefined") return undefined;
    const io = new IntersectionObserver(([e]) => setCompact(!e.isIntersecting), { threshold: 0 });
    io.observe(sentinel.current);
    return () => io.disconnect();
  }, [sticky, compactOnScroll]);

  // Publish where the header actually ENDS, in viewport coordinates, so page-level overlays can
  // start below it instead of covering it. Deriving this (topbar height + header height) is what
  // put the drawer ~17px too high: the header's top is not the topbar's bottom, because `.rc-page`
  // adds vertical padding and `.rc-page > .ft-pageheader` cancels it with a negative margin. The
  // measured edge is immune to all of that, and to the header growing, losing its tab row, or
  // going compact on scroll. Tracked on scroll as well as resize: while the header is still
  // scrolling up to its sticky position, its bottom edge moves.
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return undefined;
    let frame = 0;
    const publish = () => {
      frame = 0;
      const box = el.getBoundingClientRect();
      const root = document.documentElement.style;
      root.setProperty("--ft-pageheader-bottom", `${Math.round(box.bottom)}px`);
      // The header is full-bleed inside the content column (it cancels the page gutter with a
      // negative inline margin), so its left edge IS the content area's left edge — i.e. where
      // the sidebar ends. Overlays read this instead of the sidebar width, which lives on
      // `.ft-frame` and so does not resolve inside a body-level portal; it also follows the
      // sidebar collapsing to its 72px rail for free.
      root.setProperty("--ft-pageheader-left", `${Math.round(box.left)}px`);
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(publish);
    };
    publish();
    const ro = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(schedule);
    ro?.observe(el);
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    return () => {
      cancelAnimationFrame(frame);
      ro?.disconnect();
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      document.documentElement.style.removeProperty("--ft-pageheader-bottom");
      document.documentElement.style.removeProperty("--ft-pageheader-left");
    };
  }, [compact]);

  const handleBack = () => {
    if (onBack) return onBack();
    if (backTo) return history.push(backTo);
    return history.goBack();
  };

  // tabs: legacy `match(pathname)` support → resolve the active tab here, hand Tabs a single activePath
  const tabItems = Array.isArray(tabs) ? tabs.filter(Boolean) : [];
  const activeTab = tabItems.find((t) => {
    if (activePath != null) return t.to === activePath || t.match?.(activePath);
    if (t.exact) return pathname === t.to;
    if (t.match) return t.match(pathname);
    return pathname === t.to || pathname.startsWith(`${t.to}/`);
  });

  const actionList = Array.isArray(actions) ? actions.filter(Boolean) : null;
  const hasActions = actionList ? actionList.length > 0 : actions != null;
  const hasSubBar = tabItems.length > 0 || hasActions;

  const renderAction = (a, i, size) => {
    if (React.isValidElement(a)) return <React.Fragment key={a.key || i}>{a}</React.Fragment>;
    const { label, to, onClick, variant = "primary", size: aSize = "md", key, className: cls, ...rest } = a;
    return (
      <Button key={key || to || label || i} to={to} onClick={onClick} variant={variant} size={size || aSize} className={cls} {...rest}>
        {label}
      </Button>
    );
  };

  // phone: first action stays, the rest collapse into ⋯
  const renderActions = () => {
    if (!hasActions) return null;
    if (!actionList) return <div className="ft-pageheader__actions">{actions}</div>;
    // An action passed as an ELEMENT is a custom control — an icon button, a
    // toggle — not a labelled button. It is already compact, so it stays inline
    // on phones and only the labelled buttons collapse into the ⋯ menu.
    // Without this split, an element sitting first in the array would take the
    // one visible slot and bury the page's primary action in the menu (the
    // listings page leads with the dictate mic, followed by "+ New Stock").
    const elements = actionList.filter((a) => React.isValidElement(a));
    const buttons = actionList.filter((a) => !React.isValidElement(a));
    if (!isPhone || buttons.length <= 1) {
      return <div className="ft-pageheader__actions">{actionList.map((a, i) => renderAction(a, i))}</div>;
    }
    const [first, ...rest] = buttons;
    return (
      <div className="ft-pageheader__actions ft-pageheader__actions--phone">
        {elements.map((a, i) => renderAction(a, i))}
        {renderAction(first, 0, "lg")}
        <Popover label="More actions" side="bottom" align="end" trigger={<IconButton label="More actions" size="lg"><Icon name="MoreHorizontal" size="md" /></IconButton>}>
          {({ close }) => (
            <div className="ft-pageheader__menu">
              {rest.map((a, i) =>
                React.isValidElement(a) ? (
                  <React.Fragment key={i}>{a}</React.Fragment>
                ) : (
                  <button
                    key={a.key || a.to || a.label || i}
                    type="button"
                    className="ft-pageheader__menu-item"
                    disabled={a.disabled}
                    onClick={() => { close(); if (a.onClick) a.onClick(); else if (a.to) history.push(a.to); }}
                  >
                    {a.label}
                  </button>
                )
              )}
            </div>
          )}
        </Popover>
      </div>
    );
  };

  // Below 900px the page is named in the sticky top bar instead — see
  // pageChrome.jsx. Title, description and back button are not rendered here
  // at all on a phone; the tabs and the action row stay.
  usePublishPageChrome(chromeId, { title, backTo, onBack, showBackButton });

  // On a phone the row keeps only what has nowhere else to go.
  const hasRowContent = isAtLeastMd || !!topbarActions || !!children;

  const badgeNode = badge == null ? null : React.isValidElement(badge) ? badge : <Badge status={badge.status} tone={badge.tone} count={badge.count != null}>{badge.count != null ? badge.count : undefined}</Badge>;

  return (
    <>
      {sticky ? <div ref={sentinel} className="ft-pageheader__sentinel" aria-hidden="true" /> : null}
      <header ref={headerRef} className={["ft-pageheader", sticky ? "is-sticky" : "", compact ? "is-compact" : "", className].filter(Boolean).join(" ")}>
        {hasRowContent ? (
        <div className="ft-pageheader__row">
          {isAtLeastMd && showBackButton ? (
            <IconButton label="Back" variant="ghost" size="md" className="ft-pageheader__back" onClick={handleBack}>
              <Icon name="ArrowLeft" size="md" />
            </IconButton>
          ) : null}
          <div className="ft-pageheader__titles">
            {isAtLeastMd ? (
              <div className="ft-pageheader__titleline">
                {title != null ? <h1 className="ft-pageheader__title">{title}</h1> : null}
                {badgeNode}
              </div>
            ) : null}
            {isAtLeastMd && description ? <p className="ft-pageheader__description">{description}</p> : null}
            {children}
          </div>
          {topbarActions ? <div className="ft-pageheader__tools">{topbarActions}</div> : null}
        </div>
        ) : null}
        {hasSubBar ? (
          <div className={["ft-pageheader__sub", subBarClassName].filter(Boolean).join(" ")}>
            {tabItems.length > 0 ? (
              <Tabs
                variant="underline"
                aria-label="Section tabs"
                activePath={activeTab?.to ?? "__none__"}
                items={tabItems.map((t) => ({ label: t.label, to: t.to, exact: !!t.exact, icon: t.icon, count: t.count, countTone: t.countTone, countLabel: t.countLabel, locked: t.locked, lockedReason: t.lockedReason }))}
              />
            ) : (
              <span className="ft-pageheader__spacer" aria-hidden="true" />
            )}
            {renderActions()}
          </div>
        ) : null}
      </header>
    </>
  );
};

export default PageHeader;
